import { useState, useEffect } from 'react';
import { api } from '@/App';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Building2, Save, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const Profile = ({ user, onLogout }) => {
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    org_name: '',
    org_type: 'nonprofit',
    description: '',
    contact_person: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    focus_areas: []
  });
  const [newFocusArea, setNewFocusArea] = useState('');

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      const response = await api.get('/organizations');
      const myOrg = response.data.find(org => org.user_id === user.id);
      if (myOrg) {
        setOrganization(myOrg);
        setFormData({
          org_name: myOrg.org_name,
          org_type: myOrg.org_type || 'nonprofit',
          description: myOrg.description || '',
          contact_person: myOrg.contact_person,
          email: myOrg.email,
          phone: myOrg.phone || '',
          website: myOrg.website || '',
          address: myOrg.address || '',
          focus_areas: myOrg.focus_areas || []
        });
      }
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/organizations/${organization.id}`, formData);
      toast.success('Profile updated successfully!');
      fetchOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const addFocusArea = () => {
    if (newFocusArea.trim() && !formData.focus_areas.includes(newFocusArea.trim())) {
      setFormData({
        ...formData,
        focus_areas: [...formData.focus_areas, newFocusArea.trim()]
      });
      setNewFocusArea('');
    }
  };

  const removeFocusArea = (area) => {
    setFormData({
      ...formData,
      focus_areas: formData.focus_areas.filter(a => a !== area)
    });
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">
          <p className="text-gray-500">Loading profile...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="profile-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Organization Profile</h1>
          <p className="text-gray-600">Manage your organization's information</p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-green-600" />
                Basic Information
              </CardTitle>
              <CardDescription>Your organization's core details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org_name">Organization Name</Label>
                  <Input
                    id="org_name"
                    value={formData.org_name}
                    onChange={(e) => setFormData({ ...formData, org_name: e.target.value })}
                    required
                    data-testid="profile-org-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    required
                    data-testid="profile-contact-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Organization Type</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="org_type"
                      value="nonprofit"
                      checked={formData.org_type === 'nonprofit'}
                      onChange={(e) => setFormData({ ...formData, org_type: e.target.value })}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="text-sm">Nonprofit Organization</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="org_type"
                      value="service"
                      checked={formData.org_type === 'service'}
                      onChange={(e) => setFormData({ ...formData, org_type: e.target.value })}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="text-sm">Service Organization</span>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Tell others about your organization..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  data-testid="profile-description-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>How others can reach you</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="profile-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://example.com"
                    data-testid="profile-website-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="City, State"
                    data-testid="profile-address-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Focus Areas */}
          <Card>
            <CardHeader>
              <CardTitle>Focus Areas</CardTitle>
              <CardDescription>Topics and causes your organization works on</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Add a focus area (e.g., Education, Environment)"
                  value={newFocusArea}
                  onChange={(e) => setNewFocusArea(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFocusArea())}
                  data-testid="focus-area-input"
                />
                <Button
                  type="button"
                  onClick={addFocusArea}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="add-focus-area-button"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {formData.focus_areas.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.focus_areas.map((area, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="bg-green-100 text-green-700 pl-3 pr-2 py-1 flex items-center gap-2"
                    >
                      {area}
                      <button
                        type="button"
                        onClick={() => removeFocusArea(area)}
                        className="hover:text-green-900"
                        data-testid={`remove-focus-area-${index}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="save-profile-button"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default Profile;
